import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import { pseudoLocalise } from './pseudo';

// Every language the app can be shown in. `code` is what the account stores (LanguageRules.cs
// accepts exactly these); `tag` is the BCP 47 tag for <html lang> and for Intl-generated text
// such as "3 days ago"; `dateLocale` is what date formatting is handed. English passes undefined
// there because dates have always followed the browser's locale, and English must not change.
//
// Numbers are deliberately absent: every language keeps a full-stop decimal, matching Navigator,
// the Android app, the CSV export and the CSV import. Pass numbers into translations already
// formatted; never use i18next's number formatting.
const RELEASED = [
    { code: 'en', name: 'English', tag: 'en', dateLocale: undefined },
    { code: 'fr', name: 'Français', tag: 'fr', dateLocale: 'fr' },
    { code: 'es', name: 'Español', tag: 'es-419', dateLocale: 'es-419' },
    { code: 'pt', name: 'Português', tag: 'pt-BR', dateLocale: 'pt-BR' }
];

// Development only: accented, padded English. A string that was never extracted stays plain, and
// one that will not fit once translated overflows. The server refuses to store it.
const PSEUDO = { code: 'xx', name: 'Pseudo (dev)', tag: 'en', dateLocale: undefined };

export const LANGUAGES = process.env.NODE_ENV === 'development' ? [...RELEASED, PSEUDO] : RELEASED;

// Live for everyone since 2026-09-14: every customer-facing page is converted. Set false to
// fall back to super admins only (for example while a large batch of pages is being converted).
export const LANGUAGE_SWITCHER_LIVE = true;

const STORAGE_KEY = 'drilltrak.lang';

// English is bundled; the rest are split into their own chunks so English users download nothing.
const loaders = {
    fr: () => import('./locales/fr.json'),
    es: () => import('./locales/es.json'),
    pt: () => import('./locales/pt.json'),
    xx: async () => ({ default: pseudoLocalise(en) })
};

const byCode = (code) => LANGUAGES.find(l => l.code === code);

i18n.use(initReactI18next).init({
    resources: { en: { translation: en } },
    lng: 'en',
    fallbackLng: 'en',                      // a key missing from a translation shows English
    interpolation: { escapeValue: false },  // React already escapes
    react: { useSuspense: false },          // bundles are loaded before changeLanguage, never mid-render
    initImmediate: false                    // English is inline, so tests get it synchronously
});

i18n.on('languageChanged', (code) => {
    document.documentElement.lang = (byCode(code) || RELEASED[0]).tag;
});

export const currentLanguage = () => byCode(i18n.language) || RELEASED[0];

export async function setLanguage(code) {
    const language = byCode(code) ? code : 'en';
    if (!i18n.hasResourceBundle(language, 'translation')) {
        const bundle = await loaders[language]();
        i18n.addResourceBundle(language, 'translation', bundle.default);
    }
    await i18n.changeLanguage(language);
    try {
        window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
        // Storage blocked: this browser simply forgets, and the account's choice still applies.
    }
}

// Applies the language this browser last used, before the first render, so a reload does not
// flash English. The account's saved choice wins once getuser answers (see account.js). A bundle
// that fails to load (a deploy replaced the chunk) leaves the app in English rather than blank.
export function restoreLanguage() {
    let stored = null;
    try {
        stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return Promise.resolve();
    }
    return stored && stored !== 'en' && byCode(stored)
        ? setLanguage(stored).catch(() => { })
        : Promise.resolve();
}

export default i18n;
